import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ArtistBrandCarousel from "../components/Home/ArtistBrandCarousel";
import ServicesSection from "../components/Home/ServiceSection";
import ExploreArtistAudio from "../components/Home/ExploreArtistAudio";
import VideoProductionService from "../components/Home/VideoProductionService";
import MusicMarketing from "../components/Home/MusicMarketing";
import ResponsiveMusicGallery from "../components/Home/ResponsiveMusicGallery";
import icona from '../assets/images/hdphn_a.png';
import iconb from '../assets/images/hdphn_b.png';
import iconc from '../assets/images/hdphn_c.png';
import icond from '../assets/images/hdphn_d.png';
import videobg from '../assets/videos/home_bg_video.mp4';
import Navbar from '../components/ArtisttoHeader';
import ArtisttoFooter from "../components/ArtisttoFooter";

const useIsDesktop = (breakpoint = 768) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= breakpoint);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= breakpoint);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isDesktop;
};

const StatCard = ({ icon: Icon, number, label, isVisible, delay = 0 }) => {
  const [displayNumber, setDisplayNumber] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => {
      const targetNum = parseInt(number.replace(/\D/g, ''));
      const increment = targetNum / 60;
      let current = 0;
      const counter = setInterval(() => {
        current += increment;
        if (current >= targetNum) {
          setDisplayNumber(targetNum);
          clearInterval(counter);
        } else {
          setDisplayNumber(Math.floor(current));
        }
      }, 30);
      return () => clearInterval(counter);
    }, delay);
    return () => clearTimeout(timer);
  }, [isVisible, number, delay]);
  const suffix = number.replace(/[0-9]/g, '');
  return (
    <div className="flex flex-col items-center group  w-[186px] h-[165px] lg:h-[182px]">
      <div className="w-12 h-12 md:w-16 md:h-16 mb-4 md:mb-8 rounded-full  flex items-center justify-center  transition-all duration-300 group-hover:scale-110">
        <img
          src={Icon}
          alt="Descriptive alt text"
          className="w-[56px] h-[58px] object-contain"
        />
      </div>
      <div className="text-[20px] lg:text-4xl font-normal mb-1 md:mb-2 text-white">
        {displayNumber}{suffix}
      </div>
      <div className="text-[12px] lg:text-[18px] text-[#989898] leading-tight text-center">
        {label.split('\n').map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const location = useLocation();
  const [statsVisible, setStatsVisible] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Add mobile menu state
  const videoRef = useRef(null);
  const statsRef = useRef(null);
  
  const stats = [
    {
      icon: icona,
      number: "1000+",
      label: "Artists Community"
    },
    {
      icon: iconb,
      number: "100M+",
      label: "Views & Streams Delivered"
    },
    {
      icon: iconc,
      number: "100+",
      label: "Songs Produced & Mixed"
    },
    {
      icon: icond,
      number: "200+",
      label: "5-star Reviews"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !statsVisible) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    const currentStatsRef = statsRef.current;
    if (currentStatsRef) {
      observer.observe(currentStatsRef);
    }
    return () => {
      if (currentStatsRef) {
        observer.unobserve(currentStatsRef);
      }
    };
  }, [statsVisible]);

  // Optimize video loading
  useEffect(() => {
    // Show video after a short delay to prioritize initial render
    const timer = setTimeout(() => {
      setShowVideo(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleVideoError = () => {
    console.warn('Video failed to load, using fallback background');
    setVideoLoaded(false);
  };
  
  const isDesktop = useIsDesktop();
  const serviceRef = useRef(null);

  const scrollToServices = () => {
    console.log(serviceRef);
    serviceRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll to services if coming from Music Marketing
  useEffect(() => {
    if (location.state?.scrollToServices && serviceRef.current) {
      // Use a small delay to ensure the page is fully loaded
      setTimeout(() => {
        serviceRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
      
      // Clear the state to prevent repeated scrolling on re-renders
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  
  const menuRef = useRef(null);
  const scrollToFooter = () => {
    console.log(menuRef);
    menuRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Navigation handlers for mobile menu
  const handleNavigateToAudio = () => {
    document.getElementById('audio-video')?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const handleNavigateToMarketing = () => {
    document.getElementById('marketing')?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const handleNavigateToContact = () => {
    scrollToFooter();
    setIsMobileMenuOpen(false);
  };

  const handleJoinCommunity = () => {
    window.location.href = 'https://chat.whatsapp.com/BFxWm1sdDXJD5DFK7uF57h';
  };

  return (
    <div>
      <>
        {isDesktop ? (
          <Navbar scrollToFooter={scrollToFooter} />
        ) : (
          ''
        )}
      </>
      
      <div className="min-h-screen bg-gray-900 relative ">
        <div className="absolute inset-0 z-0 bg-black/50 overflow-hidden">
          {/* Fallback background while media loads */}
          <div className={`absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800 transition-opacity duration-1000 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`} />

          {/* Mobile: Show GIF */}
          {!isDesktop && showVideo && (
            <img
              src={'https://res.cloudinary.com/dhjzae1cm/image/upload/v1757970543/ezgif-239da22178c474_bnibc3.gif'}
              alt="Music video background"
              className={`w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
              style={{ visibility: videoLoaded ? 'visible' : 'hidden' }}
              onLoad={handleVideoLoad}
              onError={handleVideoError}
            />
          )}

          {/* Desktop: Show Video */}
          {isDesktop && showVideo && (
            <video
              controls={false}
              className={`w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'} no-overlay`}
              ref={videoRef}
              style={{ visibility: videoLoaded ? 'visible' : 'hidden' }}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedData={handleVideoLoad}
              onError={handleVideoError}
              poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Crect width='100%25' height='100%25' fill='%23000000'/%3E%3C/svg%3E"
            >
              <source src={videobg} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}

          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 min-h-[100vh] flex flex-col">
          <div className="block md:hidden">
            <Navbar 
              scrollToFooter={scrollToFooter} 
              isMobileMenuOpen={isMobileMenuOpen}
              setIsMobileMenuOpen={setIsMobileMenuOpen}
              onNavigateToAudio={handleNavigateToAudio}
              onNavigateToMarketing={handleNavigateToMarketing}
              onNavigateToContact={handleNavigateToContact}
              onJoinCommunity={handleJoinCommunity}
            />
          </div>

          <div className="flex-1 flex flex-col justify-center items-center text-center px-4 md:px-8 lg:px-12 py-2 b">
            <h1 className="text-[36px] md:text-5xl lg:text-7xl xl:text-[5.25rem] text-white leading-tight mb-6 md:mb-8 max-w-6xl font-inter mt-0 md:mt-[152px]">
              <span className="block opacity-0 animate-fade-in-up font-inter">Empowering Artists</span>
              <span className="block opacity-0 animate-fade-in-up animation-delay-300 font-inter">Elevating Music</span>
            </h1>
            <p className="text-base md:text-lg lg:text-xl xl:text-xl text-gray-200 mb-4 md:mb-6 max-w-3xl opacity-0 animate-fade-in-up animation-delay-600">
              Your journey from the underground to <br />the mainstream starts here
            </p>
            <div className=" flex w-full md:w-auto flex-col md:flex-row gap-4 mb-4 md:mb-12 opacity-0 animate-fade-in-up animation-delay-900">
              <button className=" text-[14px] lg:text-[16px] bg-[#FF8000] text-white py-[16px] px-[28px] rounded-xl  md:text-lg  hover:shadow-xl hover:shadow-orange-500/50 hover:scale-105 transition-all duration-300 text-sm md:text-[20px]  font-normal !leading-[normal]" onClick={() => window.location.href = 'https://chat.whatsapp.com/BFxWm1sdDXJD5DFK7uF57h'}>
                Join Community
              </button>
              <button className="text-[14px] lg:text-[16px] backdrop-blur-sm border border-white text-white py-[16px] px-[28px] rounded-xl md:text-lg  hover:bg-white/30 hover:border-white/50 hover:scale-105 transition-all duration-300 text-sm md:text-[20px]  font-normal !leading-[normal] " onClick={scrollToServices}>
                Explore Services
              </button>
            </div>
            <div
              ref={statsRef}
              className="flex flex-row gap-1 mt-[64px] md:mt-[0px] max-w-6xl w-full opacity-0 animate-fade-in-up animation-delay-1200  justify-center"
            >
              {stats.map((stat, index) => (
                <StatCard
                  key={index}
                  icon={stat.icon}
                  number={stat.number}
                  label={stat.label}
                  isVisible={statsVisible}
                  delay={index * 200}
                />
              ))}
            </div>
          </div>
        </div>
        <style jsx="true">{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(40px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-in-up {
            animation: fadeInUp 1s ease-out forwards;
          }
          .animation-delay-300 {
            animation-delay: 0.3s;
          }
          .animation-delay-600 {
            animation-delay: 0.6s;
          }
          .animation-delay-900 {
            animation-delay: 0.9s;
          }
          .animation-delay-1200 {
            animation-delay: 1.2s;
          }
          /* Hide WebKit overlay play button (e.g., Brave/Safari) before load */
          video::-webkit-media-controls-start-playback-button {
            display: none !important;
            -webkit-appearance: none;
          }
          video::-webkit-media-controls-overlay-play-button {
            display: none !important;
          }
        `}</style>
      </div>

      <ArtistBrandCarousel />
      <ServicesSection ref={serviceRef} />
      <ExploreArtistAudio id='audio-video' />
      <VideoProductionService />
      <MusicMarketing id='marketing' />
      <ResponsiveMusicGallery />
      <ArtisttoFooter ref={menuRef} />
    </div>
  );
};

export default Home;